export type ReadinessLevel = 'aligned' | 'ready' | 'configured' | 'active' | 'connected' | 'data_available' | 'template_ready' | 'datapoints_available' | 'products_registered' | 'applicable' | 'in_progress' | 'needs_improvement' | 'review_required' | 'needs_products' | 'needs_inventory' | 'needs_activity_data' | 'import_only' | 'empty' | 'not_applicable' | 'not_started' | 'filing_ready' | 'readiness_assessment' | 'registry_ready' | 'submission_ready' | 'tier1_in_progress' | 'tier1_ready' | 'pack_ready' | 'missing_fields' | 'needs_review' | 'not_required' | 'monitoring' | 'assurance_prep_ready' | 'gap_closure_required' | 'pcf_prep_ready' | 'overdue' | 'responded';

const READINESS_LABELS: Record<ReadinessLevel, string> = {
  aligned: 'Aligned',
  ready: 'Ready',
  configured: 'Configured',
  active: 'Active',
  connected: 'Connected',
  data_available: 'Data available',
  template_ready: 'Template ready',
  datapoints_available: 'Datapoints available',
  products_registered: 'Products registered',
  applicable: 'Applicable',
  in_progress: 'In progress',
  needs_improvement: 'Needs improvement',
  review_required: 'Review required',
  needs_products: 'Needs products',
  needs_inventory: 'Needs inventory',
  needs_activity_data: 'Needs activity data',
  import_only: 'Import only',
  empty: 'Empty',
  not_applicable: 'Not applicable',
  not_started: 'Not started',
  filing_ready: 'Filing ready',
  readiness_assessment: 'Readiness assessment',
  registry_ready: 'Registry ready',
  submission_ready: 'Submission ready',
  tier1_in_progress: 'Tier 1 in progress',
  tier1_ready: 'Tier 1 ready',
  pack_ready: 'Pack ready',
  missing_fields: 'Missing fields',
  needs_review: 'Needs review',
  not_required: 'Not required',
  monitoring: 'Monitoring',
  assurance_prep_ready: 'Assurance prep ready',
  gap_closure_required: 'Gap closure required',
  pcf_prep_ready: 'PCF prep ready',
  overdue: 'Overdue',
  responded: 'Responded',
};

const isComplianceHubPayload = (value: Record<string, unknown>): boolean =>
  'readiness' in value
  || 'indiaReadiness' in value
  || 'exportReadiness' in value
  || 'modules' in value
  || 'exportProfile' in value;

/** Unwrap compliance-hub API payloads whether nested under `data` or returned flat. */
export const unwrapComplianceHubPayload = <T extends Record<string, unknown>>(
  response: unknown
): T | null => {
  if (!response || typeof response !== 'object') {
    return null;
  }
  let body = response as Record<string, unknown>;
  for (let depth = 0; depth < 3; depth += 1) {
    if (isComplianceHubPayload(body)) {
      return body as T;
    }
    if (body.data && typeof body.data === 'object') {
      body = body.data as Record<string, unknown>;
      continue;
    }
    break;
  }
  return isComplianceHubPayload(body) ? (body as T) : null;
};

const normalizeStatusKey = (level?: string): string | undefined => {
  const trimmed = level?.trim();
  if (!trimmed) {
    return undefined;
  }
  return trimmed.toLowerCase();
};

export interface ComplianceSectionDef {
  id: string;
  label: string;
  description: string;
}

export interface ComplianceSectionGroup {
  id: string;
  label: string;
  sections: ComplianceSectionDef[];
}

export type ComplianceTrackPath =
  | '/compliance/india'
  | '/compliance/export'
  | '/compliance/geotagged-capture'
  | '/enterprise/compliance';

/** Legacy deep links that should resolve to a valid section id. */
export const COMPLIANCE_SECTION_ALIASES: Record<string, string> = {
  pat: 'pat-intensity',
  brsr: 'brsr-core',
  iso: 'iso-assurance',
  iso14064: 'iso-assurance',
  zed: 'zed-certification',
  epr: 'epr-compliance',
  pcf: 'product-pcf',
  tcfd: 'tcfd-issb',
  issb: 'tcfd-issb',
  geotagged: 'geotagged-capture',
  gps: 'geotagged-capture',
  'brsr-sg': 'brsr-core',
  'third-party-verifier': 'iso-assurance',
  'cbam-gap-closure': 'cbam',
  'epr-gap-closure': 'epr-compliance',
};

/** Standalone GPS geotagged evidence capture (all compliance workflows). */
export const GEOTAGGED_EVIDENCE_HREF = '/compliance/geotagged-capture';

export const GEOTAGGED_EVIDENCE_GROUPS: ComplianceSectionGroup[] = [
  {
    id: 'site-evidence',
    label: 'Site evidence',
    sections: [
      {
        id: 'geotagged-capture',
        label: 'GPS photo capture',
        description:
          'Automatic GPS site photos with coordinates embedded in the image for CBAM, EUDR, ISO, PAT, and buyer compliance.',
      },
    ],
  },
];

export const normalizeComplianceSectionId = (
  section: string | null | undefined,
  validSections: readonly string[]
): string | null => {
  const trimmed = section?.trim();
  if (!trimmed) {
    return null;
  }
  if (validSections.includes(trimmed)) {
    return trimmed;
  }
  const alias = COMPLIANCE_SECTION_ALIASES[trimmed.toLowerCase()];
  if (alias && validSections.includes(alias)) {
    return alias;
  }
  return null;
};

export const flattenComplianceSections = (
  groups: readonly ComplianceSectionGroup[]
): ComplianceSectionDef[] => groups.flatMap((group) => group.sections);

export const complianceSectionHref = (
  trackPath: ComplianceTrackPath,
  sectionId: string
): string => {
  if (!sectionId || sectionId === 'overview') {
    return trackPath;
  }
  return `${trackPath}?section=${encodeURIComponent(sectionId)}`;
};

export const findComplianceSectionGroup = (
  groups: readonly ComplianceSectionGroup[],
  sectionId: string
): ComplianceSectionGroup | undefined =>
  groups.find((group) => group.sections.some((section) => section.id === sectionId));

export const COMPLIANCE_PERIODS = [
  { value: 'quarter', label: 'Last quarter' },
  { value: '6months', label: 'Last 6 months' },
  { value: 'annual', label: 'Last 12 months' }
] as const;

export const INDIA_DEFAULT_SECTION = 'brsr-core';

/** India sections that now live on the page that owns the work. */
export const INDIA_RETIRED_SECTION_HREFS: Record<string, string> = {
  'sbti-plans': '/emissions-reduction-targets',
  sbti: '/emissions-reduction-targets',
  'carbon-market': '/carbon-marketplace',
  'data-sync': '/data?section=connectors',
  sync: '/data?section=connectors',
  'product-pcf': '/compliance/export?section=product-pcf',
  pcf: '/compliance/export?section=product-pcf',
};

/** Buyer questionnaires now live on Buyer audit pack. Overview opens EU export carbon. */
export const EXPORT_RETIRED_SECTION_HREFS: Record<string, string> = {
  'supplier-portal': '/buyer-audit',
  portal: '/buyer-audit',
};

export const INDIA_COMPLIANCE_GROUPS: ComplianceSectionGroup[] = [
  {
    id: 'disclosure-assurance',
    label: 'Buyer & bank proof',
    sections: [
      {
        id: 'brsr-core',
        label: 'Buyer pack (BRSR Core)',
        description:
          'What large buyers ask for: your environmental numbers in a standard India format.',
      },
      {
        id: 'iso-assurance',
        label: 'Bank / inventory proof (ISO)',
        description: 'Make your inventory ready to share with a bank, lender, or verifier.',
      },
    ],
  },
  {
    id: 'performance-targets',
    label: 'Energy & waste',
    sections: [
      {
        id: 'pat-intensity',
        label: 'Energy intensity (PAT-style)',
        description:
          'How much energy and emissions you use per output — useful for efficiency programmes.',
      },
      {
        id: 'epr-compliance',
        label: 'Waste take-back (EPR)',
        description:
          'Simple setup for packaging, e-waste, batteries, tyres, or oil take-back — registration, recycler, and recycling vs disposal amounts.',
      },
    ],
  },
  {
    id: 'products-programmes',
    label: 'Products & programmes',
    sections: [
      {
        id: 'zed-certification',
        label: 'MSME ZED certification',
        description:
          'Quality and environment certification for MSMEs — checklist, subsidy estimate, and next steps.',
      },
    ],
  },
];

export const EXPORT_DEFAULT_SECTION = 'cbam';

export const EXPORT_COMPLIANCE_GROUPS: ComplianceSectionGroup[] = [
  {
    id: 'eu-requirements',
    label: 'EU requirements',
    sections: [
      {
        id: 'cbam',
        label: 'EU export carbon (CBAM)',
        description:
          'Carbon inside goods you ship to the EU — monitoring now, registry files when ready.',
      },
      {
        id: 'csrd-supplier',
        label: 'EU buyer questions (CSRD)',
        description: 'Extra climate datapoints EU corporate customers may request from suppliers.',
      },
      {
        id: 'eudr',
        label: 'EU deforestation rules (EUDR)',
        description: 'Traceability for covered commodities (e.g. wood, rubber, coffee) sold into the EU.',
      },
    ],
  },
  {
    id: 'global-frameworks',
    label: 'Global buyer frameworks',
    sections: [
      {
        id: 'cdp',
        label: 'CDP climate questionnaire',
        description: 'Map your inventory into CDP Climate Change questions buyers may send.',
      },
      {
        id: 'gri',
        label: 'GRI emissions disclosure',
        description: 'Standard emissions disclosure language from your GHG inventory.',
      },
      {
        id: 'tcfd-issb',
        label: 'Climate risk (TCFD / ISSB)',
        description: 'Climate governance, strategy, risk, and metrics narrative structure.',
      },
    ],
  },
  {
    id: 'buyer-product-evidence',
    label: 'Buyer & product evidence',
    sections: [
      {
        id: 'ecovadis',
        label: 'EcoVadis pack',
        description: 'Pre-mapped environment and procurement datapoints for EcoVadis-style assessments.',
      },
      {
        id: 'product-pcf',
        label: 'Product LCA & PCF',
        description: 'ISO 14040/14044 LCA (multi-impact) integrated with ISO 14067 PCF — cradle-to-gate or cradle-to-grave.',
      },
    ],
  },
];

export const INDIA_COMPLIANCE_SECTIONS: ComplianceSectionDef[] =
  flattenComplianceSections(INDIA_COMPLIANCE_GROUPS);

export const EXPORT_COMPLIANCE_SECTIONS: ComplianceSectionDef[] =
  flattenComplianceSections(EXPORT_COMPLIANCE_GROUPS);

export const readinessChipColor = (
  level?: string
): 'success' | 'warning' | 'error' | 'default' | 'info' => {
  if (!level) return 'default';
  if (
    ['aligned', 'ready', 'configured', 'active', 'connected', 'data_available', 'template_ready', 'datapoints_available', 'products_registered', 'applicable', 'filing_ready', 'readiness_assessment', 'registry_ready', 'submission_ready', 'pack_ready', 'assurance_prep_ready', 'pcf_prep_ready', 'responded'].includes(level)
  ) {
    return 'success';
  }
  if (['in_progress', 'review_required', 'import_only', 'empty', 'tier1_in_progress', 'monitoring', 'needs_review', 'needs_activity_data'].includes(level)) {
    return 'warning';
  }
  if (['needs_improvement', 'needs_products', 'needs_inventory', 'needs_activity_data', 'not_started', 'missing_fields', 'gap_closure_required', 'overdue'].includes(level)) {
    return 'error';
  }
  if (['not_applicable', 'not_required'].includes(level)) return 'default';
  return 'info';
};

export const readinessLabel = (level?: string): string => {
  const normalized = normalizeStatusKey(level);
  if (!normalized || normalized === 'unknown') {
    return 'Not started';
  }
  const mapped = READINESS_LABELS[normalized as ReadinessLevel];
  if (mapped) {
    return mapped;
  }
  return normalized.replace(/_/g, ' ');
};

/** Human-readable labels for workflow rows (questionnaires, action plans). */
export const complianceWorkflowStatusLabel = (status?: string): string => {
  const normalized = normalizeStatusKey(status);
  if (!normalized || normalized === 'unknown') {
    return 'Not started';
  }
  const workflowLabels: Record<string, string> = {
    draft: 'Draft',
    sent: 'Sent',
    responded: 'Responded',
    accepted: 'Accepted',
    revision_requested: 'Revision requested',
    planned: 'Planned',
    in_progress: 'In progress',
    completed: 'Completed',
    deferred: 'Deferred',
  };
  return workflowLabels[normalized] ?? readinessLabel(status);
};
